import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useLanguage } from 'contexts/LanguageContext';
import { useCustomers } from 'hooks/useCustomers';
import { useUsers } from 'hooks/useUsers';
import { useProjects } from 'hooks/useProjects';
import { useCustomFields } from 'hooks/useCustomFields';
import CustomFieldsRenderer from 'components/common/CustomFieldsRenderer';
import { Task, TaskFormData, TaskDocument } from 'types/task';
import Modal from 'components/common/Modal';
import SearchableSelect from 'components/common/SearchableSelect';
import DocumentUpload from 'components/common/DocumentUpload';
import DateTimePicker from 'components/common/DateTimePicker';
import { XMarkIcon, ClockIcon, TagIcon, UserIcon, RectangleStackIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

interface TaskFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: TaskFormData) => void;
  initialData?: Task;
  loading?: boolean;
  preSelectedProjectId?: string; // Pre-select a project when creating tasks from project view
  onExplicitClose?: () => void; // Explicit bezárás függvény
}

const TaskForm: React.FC<TaskFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  loading = false,
  preSelectedProjectId,
  onExplicitClose
}) => {
  const { t } = useLanguage();
  const { customers } = useCustomers();
  const { users } = useUsers();
  const { projects } = useProjects();
  const { customFields } = useCustomFields('task');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, any>>(initialData?.customFields || {});

  // Dark mode detection
  useEffect(() => {
    const checkDarkMode = () => {
      setIsDarkMode(document.documentElement.classList.contains('dark'));
    };
    
    checkDarkMode();
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    
    return () => observer.disconnect();
  }, []);

  // Form kezelés
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    trigger,
    formState: { errors }
  } = useForm<TaskFormData>({
    defaultValues: {
      title: '',
      description: '',
      priority: 'medium',
      status: 'todo',
      assignedTo: '',
      customerId: '',
      dueDate: undefined,
      estimatedHours: undefined,
      manualHours: undefined,
      actualHours: undefined,
      tags: [],
      documents: []
    }
  });

  // Címkék kezelése
  const [tagInput, setTagInput] = useState('');
  const currentTags = watch('tags') || [];

  // Kiválasztott ügyfél kezelése
  const [selectedCustomer, setSelectedCustomer] = useState<string>('');

  // Kiválasztott projekt kezelése
  const [selectedProject, setSelectedProject] = useState<string>('');

  // Dátum formázás datetime-local input-hoz
  const formatDateTimeForInput = (date: Date | undefined): string => {
    if (!date) return '';

    try {
      // Ha Date objektum
      if (date instanceof Date && !isNaN(date.getTime())) {
        // Helyi időzóna szerinti formátum: YYYY-MM-DDTHH:mm
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');

        return `${year}-${month}-${day}T${hours}:${minutes}`;
      }

      return '';
    } catch (error) {
      console.error('Error formatting datetime:', error);
      return '';
    }
  };

  // Form reset when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        reset({
          title: initialData.title,
          description: initialData.description || '',
          priority: initialData.priority,
          status: initialData.status,
          assignedTo: initialData.assignedTo || '',
          customerId: initialData.customerId || '',
          projectId: initialData.projectId || '',
          dueDate: initialData.dueDate,
          estimatedHours: initialData.estimatedHours,
          manualHours: initialData.manualHours,
          actualHours: initialData.actualHours,
          tags: initialData.tags || [],
          documents: initialData.documents || []
        });
        setSelectedCustomer(initialData.customerId || '');
        setSelectedProject(initialData.projectId || preSelectedProjectId || '');
        setCustomFieldValues(initialData.customFields || {});
      } else {
        reset({
          title: '',
          description: '',
          priority: 'medium',
          status: 'todo',
          assignedTo: '',
          customerId: '',
          projectId: preSelectedProjectId || '',
          dueDate: undefined,
          estimatedHours: undefined,
          manualHours: undefined,
          actualHours: undefined,
          tags: [],
          documents: []
        });
        setSelectedCustomer('');
        setSelectedProject(preSelectedProjectId || '');
        setCustomFieldValues({});
      }
      setTagInput('');
    }
  }, [isOpen, initialData, preSelectedProjectId]);

  // Ügyfél kiválasztás kezelése
  const handleCustomerSelect = (customerId: string) => {
    setSelectedCustomer(customerId);
    setValue('customerId', customerId);
  };

  // Projekt kiválasztás kezelése
  const handleProjectSelect = (projectId: string) => {
    setSelectedProject(projectId);
    setValue('projectId', projectId);
  };

  // Címke hozzáadása
  const handleAddTag = () => {
    if (tagInput.trim() && !currentTags.includes(tagInput.trim())) {
      const newTags = [...currentTags, tagInput.trim()];
      setValue('tags', newTags);
      setTagInput('');
    }
  };

  // Címke eltávolítása
  const handleRemoveTag = (tagToRemove: string) => {
    const newTags = currentTags.filter(tag => tag !== tagToRemove);
    setValue('tags', newTags);
  };

  // Enter lenyomás kezelése címkék hozzáadásához
  const handleTagKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  // Form submit
  const onSubmitHandler = async (data: TaskFormData) => {
    setIsSubmitting(true);

    try {
      // Ügyfél ID hozzáadása
      if (selectedCustomer) {
        data.customerId = selectedCustomer;
      }

      // Projekt ID hozzáadása
      if (selectedProject) {
        data.projectId = selectedProject;
      } else if (preSelectedProjectId) {
        data.projectId = preSelectedProjectId;
      }

      // Egyedi mezők hozzáadása
      data.customFields = customFieldValues;

      await onSubmit(data);

      // Sikeres mentés toast
      if (initialData) {
        toast.success(t('tasks.messages.updateSuccess') || 'Feladat sikeresen frissítve!');
      } else {
        toast.success(t('tasks.messages.createSuccess') || 'Feladat sikeresen létrehozva!');
      }

      // A parent komponens (ProjectTasksModal) kezeli a modal bezárását
    } catch (error) {
      console.error('Error submitting task:', error);
      const errorMessage = initialData 
        ? (t('tasks.messages.updateError') || 'Hiba történt a feladat frissítése során')
        : (t('tasks.messages.createError') || 'Hiba történt a feladat létrehozása során');
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Ügyfél opciók
  const customerOptions = customers.map(customer => ({
    value: customer.id,
    label: customer.type === 'organization'
      ? customer.companyName
      : `${customer.firstName} ${customer.lastName}`
  }));

  // Projekt opciók (csak aktív projektek)
  const projectOptions = projects
    .filter(project => project.status === 'active' || project.status === 'planning')
    .map(project => ({
      value: project.id,
      label: project.name
    }));

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose();
      }}
      onExplicitClose={() => {
        if (onExplicitClose) {
          onExplicitClose();
        } else {
          onClose();
        }
      }}
      title={initialData ? t('tasks.editTask') : t('tasks.createTask')}
      size="6xl"
      zIndex={50}
    >
      <form
        onSubmit={handleSubmit(onSubmitHandler)}
        className="space-y-2 md:space-y-6 p-2 md:p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-white dark:bg-gray-800">
          <div className="space-y-2 md:space-y-6">
            {/* Alapadatok */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
              <div className="flex">
                <div className="hidden md:flex p-4 bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/20 items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
                  <ClockIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="p-2 md:p-4 flex-1">
                  <h3 className="text-xs md:text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2 md:mb-4 flex items-center">
                    <ClockIcon className="w-3.5 h-3.5 md:w-4 md:h-4 text-blue-600 dark:text-blue-400 mr-1.5 md:mr-2 md:hidden" />
                    {t('tasks.sections.basicInfo')}
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4">
                    {/* Feladat neve */}
                    <div className="md:col-span-2">
                      <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                        {t('tasks.fields.title')} *
                      </label>
                      <input
                        {...register('title', { required: t('tasks.validation.titleRequired') })}
                        type="text"
                        className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md md:rounded-lg px-2.5 md:px-3 py-1.5 md:py-2 text-sm text-gray-900 dark:text-gray-100 
                                 placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                        placeholder={t('tasks.placeholders.taskTitle')}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        onFocus={(e) => {
                          e.stopPropagation();
                        }}
                      />
                      {errors.title && (
                        <p className="text-red-600 dark:text-red-400 text-sm mt-1">{errors.title.message}</p>
                      )}
                    </div>

                    {/* Feladat szám (csak szerkesztésnél) */}
                    {initialData && initialData.taskNumber && (
                      <div className="md:col-span-2">
                        <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                          {t('tasks.fields.taskNumber')}
                        </label>
                        <input
                          value={initialData.taskNumber}
                          type="text"
                          readOnly
                          className="w-full bg-gray-50 dark:bg-gray-600 border border-gray-300 dark:border-gray-600 rounded-md md:rounded-lg px-2.5 md:px-3 py-1.5 md:py-2 text-sm text-gray-900 dark:text-gray-100 
                                   cursor-not-allowed"
                        />
                      </div>
                    )}

                    {/* Prioritás */}
                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                        {t('tasks.fields.priority')}
                      </label>
                      <select
                        {...register('priority')}
                        className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md md:rounded-lg px-2.5 md:px-3 py-1.5 md:py-2 text-sm text-gray-900 dark:text-gray-100 
                                 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                        onClick={(e) => e.stopPropagation()}
                        onFocus={(e) => e.stopPropagation()}
                      >
                        <option value="low">{t('tasks.priority.low')}</option>
                        <option value="medium">{t('tasks.priority.medium')}</option>
                        <option value="high">{t('tasks.priority.high')}</option>
                        <option value="urgent">{t('tasks.priority.urgent')}</option>
                      </select>
                    </div>

                    {/* Státusz */}
                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                        {t('tasks.fields.status')}
                      </label>
                      <select
                        {...register('status')}
                        className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md md:rounded-lg px-2.5 md:px-3 py-1.5 md:py-2 text-sm text-gray-900 dark:text-gray-100 
                                 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                        onClick={(e) => e.stopPropagation()}
                        onFocus={(e) => e.stopPropagation()}
                      >
                        <option value="todo">{t('tasks.status.todo')}</option>
                        <option value="in_progress">{t('tasks.status.in_progress')}</option>
                        <option value="completed">{t('tasks.status.completed')}</option>
                        <option value="cancelled">{t('tasks.status.cancelled')}</option>
                      </select>
                    </div>

                    {/* Határidő - Modern DateTimePicker */}
                    <div className="md:col-span-2">
                      <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                        {t('tasks.fields.dueDate')}
                      </label>
                      <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}>
                        <DateTimePicker
                          value={watch('dueDate')}
                          onChange={(date) => setValue('dueDate', date)}
                          placeholder={t('tasks.placeholders.selectDueDate') || 'Válassz határidőt'}
                          showTime={true}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Leírás */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
              <div className="flex">
                <div className="hidden md:flex p-4 bg-gradient-to-br from-green-100 to-emerald-100 dark:from-green-900/20 dark:to-emerald-900/20 items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
                  <TagIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div className="p-2 md:p-4 flex-1">
                  <h3 className="text-xs md:text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2 md:mb-4 flex items-center">
                    <TagIcon className="w-3.5 h-3.5 md:w-4 md:h-4 text-green-600 dark:text-green-400 mr-1.5 md:mr-2 md:hidden" />
                    {t('tasks.sections.description')}
                  </h3>

                  <div className="space-y-2 md:space-y-4">
                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                        {t('tasks.fields.description')}
                      </label>
                      <textarea
                        {...register('description')}
                        rows={5}
                        className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md md:rounded-lg px-2.5 md:px-3 py-1.5 md:py-2 text-sm text-gray-900 dark:text-gray-100 
                                 placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500
                                 font-mono leading-relaxed resize-y min-h-[100px] md:min-h-[200px] max-h-[250px] md:max-h-[400px]"
                        placeholder={t('tasks.placeholders.taskDescription')}
                        onClick={(e) => e.stopPropagation()}
                        onFocus={(e) => e.stopPropagation()}
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        💡 A sortörések megmaradnak a mentés után
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Hozzárendelés */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
              <div className="flex">
                <div className="hidden md:flex p-4 bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900/20 dark:to-pink-900/20 items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
                  <UserIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="p-2 md:p-4 flex-1">
                  <h3 className="text-xs md:text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2 md:mb-4 flex items-center">
                    <UserIcon className="w-3.5 h-3.5 md:w-4 md:h-4 text-purple-600 dark:text-purple-400 mr-1.5 md:mr-2 md:hidden" />
                    {t('tasks.sections.assignment')}
                  </h3>

                  <div className="space-y-2 md:space-y-4">
                    {/* Felelős */}
                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                        {t('tasks.fields.assignedTo')}
                      </label>
                      <div onClick={(e) => e.stopPropagation()}>
                        <SearchableSelect
                          options={users.map(user => ({
                            value: user.id,
                            label: `${user.displayName} (${user.email})`
                          }))}
                          value={watch('assignedTo')}
                          onChange={(value) => setValue('assignedTo', value)}
                          placeholder={t('tasks.placeholders.selectAssignee')}
                          searchPlaceholder={t('tasks.placeholders.searchAssignee')}
                        />
                      </div>
                    </div>

                    {/* Ügyfél */}
                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                        {t('tasks.fields.customer')}
                      </label>
                      <div onClick={(e) => e.stopPropagation()}>
                        <SearchableSelect
                          options={customerOptions}
                          value={selectedCustomer}
                          onChange={handleCustomerSelect}
                          placeholder={t('tasks.placeholders.selectCustomer')}
                          searchPlaceholder={t('tasks.placeholders.searchCustomer')}
                        />
                      </div>
                    </div>

                    {/* Projekt */}
                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                        {t('tasks.fields.project')}
                      </label>
                      <div onClick={(e) => e.stopPropagation()}>
                        <SearchableSelect
                          options={projectOptions}
                          value={selectedProject}
                          onChange={handleProjectSelect}
                          placeholder={t('tasks.placeholders.selectProject')}
                          searchPlaceholder={t('tasks.placeholders.searchProject')}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Időbecslés */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
              <div className="flex">
                <div className="hidden md:flex p-4 bg-gradient-to-br from-orange-100 to-red-100 dark:from-orange-900/20 dark:to-red-900/20 items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
                  <ClockIcon className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                </div>
                <div className="p-2 md:p-4 flex-1">
                  <h3 className="text-xs md:text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2 md:mb-4 flex items-center">
                    <ClockIcon className="w-3.5 h-3.5 md:w-4 md:h-4 text-orange-600 dark:text-orange-400 mr-1.5 md:mr-2 md:hidden" />
                    {t('tasks.sections.timeEstimation')}
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4">
                    {/* Becsült órák */}
                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                        {t('tasks.fields.estimatedHours')}
                      </label>
                      <input
                        {...register('estimatedHours', {
                          valueAsNumber: true,
                          min: { value: 0, message: t('tasks.validation.estimatedHoursMin') }
                        })}
                        type="number"
                        step="0.5"
                        min="0"
                        className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md md:rounded-lg px-2.5 md:px-3 py-1.5 md:py-2 text-sm text-gray-900 dark:text-gray-100 
                                 placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                        placeholder="0"
                        onClick={(e) => e.stopPropagation()}
                        onFocus={(e) => e.stopPropagation()}
                      />
                      {errors.estimatedHours && (
                        <p className="text-red-600 dark:text-red-400 text-sm mt-1">{errors.estimatedHours.message}</p>
                      )}
                    </div>

                    {/* Manuális órák */}
                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                        🖊️ {t('tasks.fields.manualHours') || 'Manuális órák'}
                      </label>
                      <input
                        {...register('manualHours', {
                          valueAsNumber: true,
                          min: { value: 0, message: t('tasks.validation.manualHoursMin') || 'A manuális órák száma nem lehet negatív' }
                        })}
                        type="number"
                        step="0.5"
                        min="0"
                        className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md md:rounded-lg px-2.5 md:px-3 py-1.5 md:py-2 text-sm text-gray-900 dark:text-gray-100 
                                 placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                        placeholder="0"
                        onClick={(e) => e.stopPropagation()}
                        onFocus={(e) => e.stopPropagation()}
                      />
                      {errors.manualHours && (
                        <p className="text-red-600 dark:text-red-400 text-sm mt-1">{errors.manualHours.message}</p>
                      )}
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        💡 Gyorsan felvihető tényleges órák (hozzáadódik az időmérős órákhoz)
                      </p>
                    </div>

                  </div>
                </div>
              </div>
            </div>

            {/* Címkék */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
              <div className="flex">
                <div className="hidden md:flex p-4 bg-gradient-to-br from-cyan-100 to-blue-100 dark:from-cyan-900/20 dark:to-blue-900/20 items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
                  <TagIcon className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
                </div>
                <div className="p-2 md:p-4 flex-1">
                  <h3 className="text-xs md:text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2 md:mb-4 flex items-center">
                    <TagIcon className="w-3.5 h-3.5 md:w-4 md:h-4 text-cyan-600 dark:text-cyan-400 mr-1.5 md:mr-2 md:hidden" />
                    {t('tasks.sections.tags')}
                  </h3>

                  <div className="space-y-2 md:space-y-4">
                    {/* Címke hozzáadás */}
                    <div className="flex flex-col sm:flex-row gap-2">
                      <input
                        type="text"
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyPress={handleTagKeyPress}
                        className="flex-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md md:rounded-lg px-2.5 md:px-3 py-1.5 md:py-2 text-sm text-gray-900 dark:text-gray-100 
                                 placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                        placeholder={t('tasks.placeholders.addTag')}
                        onClick={(e) => e.stopPropagation()}
                        onFocus={(e) => e.stopPropagation()}
                      />
                      <button
                        type="button"
                        onClick={handleAddTag}
                        className="w-full sm:w-auto px-3 md:px-4 py-1.5 md:py-2 bg-primary-600 text-white text-sm rounded-md md:rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        {t('tasks.actions.addTag')}
                      </button>
                    </div>

                    {/* Meglévő címkék */}
                    {currentTags.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {currentTags.map((tag, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2 py-1 bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-full text-sm"
                          >
                            {tag}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveTag(tag);
                              }}
                              className="ml-1 text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400"
                            >
                              <XMarkIcon className="w-4 h-4" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Dokumentumok feltöltése */}
            <DocumentUpload
              documents={watch('documents') || []}
              onDocumentsChange={(documents) => setValue('documents', documents)}
              uploadPath={`tasks/${initialData?.id || 'temp'}/documents`}
              title={t('tasks.fields.documents') || 'Dokumentumok'}
              subtitle={t('tasks.placeholders.documents') || 'Csatolja a feladathoz kapcsolódó dokumentumokat'}
              disabled={isSubmitting || loading}
            />

            {/* Egyedi mezők */}
            {customFields.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center mb-4">
                  <div className="p-2 bg-gradient-to-br from-purple-100 to-indigo-100 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-lg mr-3">
                    <RectangleStackIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      Egyedi mezők
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Feladat specifikus egyedi adatok
                    </p>
                  </div>
                </div>
                <CustomFieldsRenderer
                  customFields={customFields}
                  values={customFieldValues}
                  onChange={(key, value) => setCustomFieldValues(prev => ({ ...prev, [key]: value }))}
                  disabled={isSubmitting || loading}
                />
              </div>
            )}
          </div>
        </div>

        {/* Gombok */}
        <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 pt-2 md:pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (onExplicitClose) {
                onExplicitClose();
              } else {
                onClose();
              }
            }}
            className="w-full sm:w-auto px-3 md:px-4 py-2 md:py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md md:rounded-lg 
                     hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            disabled={isSubmitting || loading}
            onClick={async (e) => {
              e.preventDefault();
              e.stopPropagation();

              // Explicit form submit - nem a native submit event-et használjuk
              const formData = watch();

              // Validáció ellenőrzés
              const isValid = await trigger();
              if (isValid) {
                await onSubmitHandler(formData);
              }
            }}
            className="w-full sm:w-auto px-3 md:px-4 py-2 md:py-2 text-sm font-medium text-white bg-primary-600 rounded-md md:rounded-lg 
                     hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 
                     disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {isSubmitting || loading ? t('common.saving') : (initialData ? t('common.update') : t('common.create'))}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default TaskForm; 